import { Plus, Trash2 } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Row, Col, Input, Button, FormInstance } from 'antd';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import { InputNumber, InputDecimal } from '@/components/_common';

const round = (num: number, places: number) => {
  if (!`${num}`.includes('e')) {
    return +`${Math.round(Number(`${num}e+${places}`))}e-${places}`;
  }
  const arr = `${num}`.split('e');

  let sig = '';

  if (+arr[1] + places > 0) {
    sig = '+';
  }

  return +`${Math.round(
    Number(`${+arr[0]}e${sig}${+arr[1] + places}`),
  )}e-${places}`;
};

type Installment = {
  numberOfDay: number;
  percentage: number;
};

type FormValues = {
  installments: Installment[];
  name: string;
  percentage?: number;
  qtdIntallments?: number;
  intervalDays?: number;
};

type Props = {
  form: FormInstance<FormValues>;
};

function PaymentConditionsForm({ form }: Props) {
  const intl = useIntl();

  const defaultEmptyFieldError = {
    required: true,
    message: intl.formatMessage({
      id: 'commons.form.errors.requiredField',
    }),
  };

  const handleFormList = (currLength: number) => {
    const percentage = round(100 / currLength, 2);
    const intervalDays = form.getFieldValue('intervalDays');

    form.setFieldsValue({
      qtdIntallments: currLength,
      installments: new Array(currLength)
        .fill({
          numberOfDays: intervalDays,
          percentage,
        })
        .map((item, index) => {
          const numberOfDays = item.numberOfDays * (index + 1);

          if (currLength - 1 === index) {
            return {
              numberOfDays,
              percentage: round(100 - percentage * currLength + percentage, 2),
            };
          }

          return {
            ...item,
            numberOfDays,
          };
        }),
    });

    form.validateFields(['installments']);
  };

  return (
    <Form
      preserve={true}
      form={form}
      onValuesChange={(valueChanged, fieldsChanged) => {
        if (valueChanged.installments) {
          form.validateFields(['installments']);
        }
        if (valueChanged.intervalDays) {
          const currLength = Math.floor(
            !fieldsChanged.qtdIntallments || fieldsChanged.qtdIntallments < 1
              ? 1
              : fieldsChanged.qtdIntallments,
          );

          handleFormList(currLength);
        }
      }}
    >
      <Row gutter={24}>
        <Col span={24} sm={12}>
          <FloatingFormItem
            name="name"
            rules={[defaultEmptyFieldError]}
            label={intl.formatMessage({
              id: 'paymentConditions.domain.name',
            })}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12}>
          <FloatingFormItem
            name="percentage"
            label={intl.formatMessage({
              id: 'paymentConditions.domain.percentage',
            })}
            initialValue={0}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12}>
          <FloatingFormItem
            initialValue={1}
            name="qtdIntallments"
            label="Quantidade de parcelas"
            getValueFromEvent={(qtd) => {
              const currLength = Math.floor(!qtd || qtd < 1 ? 1 : qtd);

              handleFormList(currLength);

              return currLength;
            }}
          >
            <InputNumber min={1} />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12}>
          <FloatingFormItem
            initialValue={30}
            name="intervalDays"
            label={intl.formatMessage({
              id: 'paymentConditions.domain.installments.intervalDays',
            })}
          >
            <InputNumber />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <h2>
            <FormattedMessage id="paymentConditions.domain.installments" />
          </h2>

          <Form.List
            name="installments"
            initialValue={[
              {
                numberOfDays: 30,
                percentage: 100,
              },
            ]}
            rules={[
              {
                validator: async (_rule, values: Installment[]) => {
                  if (!values || values.length < 1) {
                    return Promise.reject(
                      new Error(
                        intl.formatMessage({
                          id: 'paymentConditions.form.installments.rules.minimum',
                        }),
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: async (_rule, values: Installment[]) => {
                  let sumPercentage = 0;

                  values.forEach((item) => {
                    sumPercentage += item.percentage;
                  });

                  sumPercentage = round(sumPercentage, 2);

                  if (sumPercentage !== 100) {
                    return Promise.reject(
                      new Error(
                        intl.formatMessage({
                          id: 'paymentConditions.form.installments.rules.mustBe100',
                        }),
                      ),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            {(fields, _, { errors }) => (
              <>
                <Form.ErrorList errors={errors} />

                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={24}>
                    <Col flex="auto">
                      <FloatingFormItem
                        {...restField}
                        parentFieldName="installments"
                        name={[name, 'numberOfDays']}
                        label={intl.formatMessage({
                          id: 'paymentConditions.domain.installments.intervalDays',
                        })}
                        rules={[defaultEmptyFieldError]}
                      >
                        <InputNumber style={{ width: '100%' }} />
                      </FloatingFormItem>
                    </Col>

                    <Col flex="auto">
                      <FloatingFormItem
                        {...restField}
                        parentFieldName="installments"
                        name={[name, 'percentage']}
                        label={intl.formatMessage({
                          id: 'paymentConditions.domain.installments.percentage',
                        })}
                        rules={[defaultEmptyFieldError]}
                      >
                        <InputDecimal />
                      </FloatingFormItem>
                    </Col>

                    <Col flex="64px" style={{ marginTop: '8px' }}>
                      {key !== 0 && (
                        <Button
                          type="link"
                          onClick={() => {
                            const currLength = fields.length - 1;

                            handleFormList(currLength);
                          }}
                        >
                          <AntdFeatherIcon icon={<Trash2 />} />
                        </Button>
                      )}
                    </Col>
                  </Row>
                ))}

                <Button
                  type="link"
                  onClick={() => {
                    const currLength = fields.length + 1;

                    handleFormList(currLength);
                  }}
                  icon={<AntdFeatherIcon icon={<Plus />} size={12} />}
                >
                  <FormattedMessage id="paymentConditions.domain.installments.add" />
                </Button>
              </>
            )}
          </Form.List>
        </Col>
      </Row>
    </Form>
  );
}

export default PaymentConditionsForm;
